import { baseApi } from "@/api/baseApi";
import { SUPPLIER_TAGS } from "@/api/suppliers.constraints";
import { SPEND_TAGS } from "@/api/spend.constraints";
import type {
  ContractFamilyAuditEventPage,
  ContractFamilyAuditLogParams,
  ContractFamilyMetadataRequest,
  ContractFamilyNoteCreateRequest,
  ContractFamilyNotePage,
  ContractFamilyNotesParams,
  SupplierContactsRequest,
  SupplierContactsPreview,
  SupplierContactsPreviewParams,
  SupplierContractFolderRef,
  SupplierCreateRequest,
  SupplierDetail,
  SupplierFamilyCandidateParams,
  SupplierFamilyListParams,
  SupplierFamilyPage,
  SupplierListParams,
  SupplierPage,
  SupplierPatchRequest,
  SupplierContractValueAggregate,
} from "@/types/supplier";
import type { DirectoryUser } from "@/types/user";

const SUPPLIERS_BASE_URL = "/pf-mainweb/suppliers";

interface SupplierIdArg {
  supplierId: string;
}

interface SupplierFamilyArg extends SupplierIdArg {
  familyId: string;
}

interface SupplierUpdateArg extends SupplierIdArg {
  data: SupplierPatchRequest;
}

interface SupplierFamiliesArg extends SupplierIdArg {
  params?: SupplierFamilyListParams;
}

interface SupplierContractValuesArg extends SupplierIdArg {
  includeAll?: boolean;
}

interface SupplierAssignmentArg extends SupplierFamilyArg {
  data?: ContractFamilyMetadataRequest;
}

interface SupplierUploadFolderArg extends SupplierIdArg {
  name: string;
  data?: Omit<ContractFamilyMetadataRequest, "name">;
}

interface SupplierFamilyMetadataArg extends SupplierFamilyArg {
  data: ContractFamilyMetadataRequest;
}

interface SupplierFamilyNotesArg extends SupplierFamilyArg {
  params?: ContractFamilyNotesParams;
}

interface SupplierFamilyAuditLogArg extends SupplierFamilyArg {
  params?: ContractFamilyAuditLogParams;
}

interface SupplierFamilyNoteCreateArg extends SupplierFamilyArg {
  data: ContractFamilyNoteCreateRequest;
}

interface SupplierContactsArg extends SupplierIdArg {
  data: SupplierContactsRequest;
}

interface SupplierContactsPreviewArg extends SupplierIdArg {
  params?: SupplierContactsPreviewParams;
}

const resourcePath = (...resourceIds: string[]) => resourceIds.map(encodeURIComponent).join("/");

const withSearchParams = (url: string, values: object) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    // A list repeats its parameter once per value (`?division=a&division=b`).
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item !== null && item !== undefined && item !== "") searchParams.append(key, String(item));
    }
  }
  const query = searchParams.toString();
  return query ? `${url}?${query}` : url;
};

export const supplierRequests = {
  list: (params: SupplierListParams = {}) => ({
    url: withSearchParams(`${SUPPLIERS_BASE_URL}/`, params),
    method: "GET" as const,
  }),
  detail: (supplierId: string) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}`,
    method: "GET" as const,
  }),
  families: ({ supplierId, params = {} }: SupplierFamiliesArg) => ({
    url: withSearchParams(`${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders`, params),
    method: "GET" as const,
  }),
  candidates: (params: SupplierFamilyCandidateParams = {}) => ({
    url: withSearchParams(`${SUPPLIERS_BASE_URL}/contract-folders/candidates`, params),
    method: "GET" as const,
  }),
  owners: () => ({
    url: `${SUPPLIERS_BASE_URL}/owners`,
    method: "GET" as const,
  }),
  familyCategories: () => ({
    url: `${SUPPLIERS_BASE_URL}/family-categories`,
    method: "GET" as const,
  }),
  create: (data: SupplierCreateRequest) => ({
    url: `${SUPPLIERS_BASE_URL}/`,
    method: "POST" as const,
    data,
  }),
  update: ({ supplierId, data }: SupplierUpdateArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}`,
    method: "PATCH" as const,
    data,
  }),
  archive: (supplierId: string) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/archive`,
    method: "POST" as const,
  }),
  restore: (supplierId: string) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/restore`,
    method: "POST" as const,
  }),
  values: ({ supplierId, includeAll = false }: SupplierContractValuesArg) => ({
    url: withSearchParams(`${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-values`, {
      include_all: includeAll ? true : undefined,
    }),
    method: "GET" as const,
  }),
  assign: ({ supplierId, familyId, data = {} }: SupplierAssignmentArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/link`,
    method: "POST" as const,
    data: { fs_entry_id: familyId, ...data },
  }),
  createUploadFolder: ({ supplierId, name, data = {} }: SupplierUploadFolderArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/upload`,
    method: "POST" as const,
    data: { name, ...data },
  }),
  family: ({ supplierId, familyId }: SupplierFamilyArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/${resourcePath(familyId)}`,
    method: "GET" as const,
  }),
  familyByFse: (fseId: string) => ({
    url: `${SUPPLIERS_BASE_URL}/contract-folders/by-fse/${resourcePath(fseId)}`,
    method: "GET" as const,
  }),
  detach: ({ supplierId, familyId }: SupplierFamilyArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/${resourcePath(familyId)}`,
    method: "DELETE" as const,
  }),
  updateFamilyMetadata: ({ supplierId, familyId, data }: SupplierFamilyMetadataArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/${resourcePath(familyId)}`,
    method: "PATCH" as const,
    data,
  }),
  familyNotes: ({ supplierId, familyId, params = {} }: SupplierFamilyNotesArg) => ({
    url: withSearchParams(
      `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/${resourcePath(familyId)}/notes`,
      params,
    ),
    method: "GET" as const,
  }),
  familyAuditLog: ({ supplierId, familyId, params = {} }: SupplierFamilyAuditLogArg) => ({
    url: withSearchParams(
      `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/${resourcePath(familyId)}/audit-log`,
      params,
    ),
    method: "GET" as const,
  }),
  addFamilyNote: ({ supplierId, familyId, data }: SupplierFamilyNoteCreateArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contract-folders/${resourcePath(familyId)}/notes`,
    method: "POST" as const,
    data,
  }),
  contacts: ({ supplierId, data }: SupplierContactsArg) => ({
    url: `${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contacts`,
    method: "PUT" as const,
    data,
  }),
  contactsPreview: ({ supplierId, params = {} }: SupplierContactsPreviewArg) => ({
    url: withSearchParams(`${SUPPLIERS_BASE_URL}/${resourcePath(supplierId)}/contacts`, params),
    method: "GET" as const,
  }),
};

const supplierListTag = { type: SUPPLIER_TAGS.SUPPLIERS, id: "LIST" } as const;
const supplierOwnerOptionsTag = { type: SUPPLIER_TAGS.SUPPLIER_OWNER_OPTIONS, id: "LIST" } as const;
const supplierCandidateTag = { type: SUPPLIER_TAGS.SUPPLIER_CANDIDATES, id: "LIST" } as const;
const supplierDetailTag = (supplierId: string) => ({ type: SUPPLIER_TAGS.SUPPLIER_DETAIL, id: supplierId }) as const;
const supplierFamiliesTag = (supplierId: string) =>
  ({ type: SUPPLIER_TAGS.SUPPLIER_FAMILIES, id: supplierId }) as const;
const supplierFamilyNotesTag = (supplierId: string, familyId: string) =>
  ({ type: SUPPLIER_TAGS.SUPPLIER_FAMILY_NOTES, id: `${supplierId}:${familyId}` }) as const;
const supplierValuesTag = (supplierId: string) => ({ type: SUPPLIER_TAGS.SUPPLIER_VALUES, id: supplierId }) as const;

export const supplierInvalidationTags = {
  detail: (supplierId: string) => [
    supplierListTag,
    supplierOwnerOptionsTag,
    supplierDetailTag(supplierId),
    SPEND_TAGS.SUPPLIERS,
    SPEND_TAGS.OWNERS,
  ],
  assignment: ({ supplierId }: SupplierAssignmentArg) => [
    supplierListTag,
    supplierOwnerOptionsTag,
    supplierFamiliesTag(supplierId),
    supplierCandidateTag,
    supplierDetailTag(supplierId),
    supplierValuesTag(supplierId),
    SPEND_TAGS.SUPPLIERS,
    SPEND_TAGS.FAMILIES,
    SPEND_TAGS.SERIES,
  ],
  detach: (supplierId: string) => [
    supplierListTag,
    supplierOwnerOptionsTag,
    supplierFamiliesTag(supplierId),
    supplierCandidateTag,
    supplierDetailTag(supplierId),
    supplierValuesTag(supplierId),
    SPEND_TAGS.SUPPLIERS,
    SPEND_TAGS.FAMILIES,
    SPEND_TAGS.SERIES,
  ],
  familyMetadata: (supplierId: string) => [
    supplierListTag,
    supplierOwnerOptionsTag,
    supplierFamiliesTag(supplierId),
    supplierCandidateTag,
    supplierValuesTag(supplierId),
    SPEND_TAGS.SUPPLIERS,
    SPEND_TAGS.FAMILIES,
    SPEND_TAGS.SERIES,
  ],
  contacts: (supplierId: string) => [supplierDetailTag(supplierId), supplierFamiliesTag(supplierId)],
  familyNotes: (supplierId: string, familyId: string) => [supplierFamilyNotesTag(supplierId, familyId)],
};

export const suppliersApi = baseApi.injectEndpoints({
  overrideExisting: false,
  endpoints: builder => ({
    getSupplierOwnerOptions: builder.query<DirectoryUser[], void>({
      providesTags: [supplierListTag, supplierOwnerOptionsTag],
      query: supplierRequests.owners,
    }),
    getSupplierFamilyCategories: builder.query<string[], void>({
      providesTags: [supplierListTag],
      query: supplierRequests.familyCategories,
    }),
    getSuppliers: builder.query<SupplierPage, SupplierListParams | void>({
      providesTags: result => [
        supplierListTag,
        ...(result?.items.map(supplier => supplierDetailTag(supplier.id)) ?? []),
      ],
      query: params => supplierRequests.list(params ?? {}),
    }),
    getSupplier: builder.query<SupplierDetail, string>({
      providesTags: (_result, _error, supplierId) => [supplierDetailTag(supplierId)],
      query: supplierRequests.detail,
    }),
    getSupplierFamilies: builder.query<SupplierFamilyPage, SupplierFamiliesArg>({
      providesTags: (_result, _error, { supplierId }) => [supplierFamiliesTag(supplierId)],
      query: supplierRequests.families,
    }),
    getSupplierContractFolder: builder.query<SupplierFamilyPage["items"][number], SupplierFamilyArg>({
      providesTags: (_result, _error, { supplierId }) => [supplierFamiliesTag(supplierId)],
      query: supplierRequests.family,
    }),
    // 404 for a folder that is not a readable supplier contract folder.
    getSupplierContractFolderByFse: builder.query<SupplierContractFolderRef, string>({
      providesTags: result => (result ? [supplierFamiliesTag(result.supplier_id)] : []),
      query: supplierRequests.familyByFse,
    }),
    getSupplierContractValues: builder.query<SupplierContractValueAggregate, SupplierContractValuesArg>({
      providesTags: (_result, _error, { supplierId }) => [supplierValuesTag(supplierId)],
      query: supplierRequests.values,
    }),
    getSupplierContactsPreview: builder.query<SupplierContactsPreview, SupplierContactsPreviewArg>({
      providesTags: (_result, _error, { supplierId }) => [
        supplierDetailTag(supplierId),
        supplierFamiliesTag(supplierId),
      ],
      query: supplierRequests.contactsPreview,
    }),
    getSupplierFamilyNotes: builder.query<ContractFamilyNotePage, SupplierFamilyNotesArg>({
      providesTags: (_result, _error, { supplierId, familyId }) => [supplierFamilyNotesTag(supplierId, familyId)],
      query: supplierRequests.familyNotes,
    }),
    // Family mutations made here invalidate the families tag; changes made elsewhere (document
    // uploads, summary edits) show because the log refetches whenever it opens.
    getSupplierFamilyAuditLog: builder.query<ContractFamilyAuditEventPage, SupplierFamilyAuditLogArg>({
      providesTags: (_result, _error, { supplierId }) => [supplierFamiliesTag(supplierId)],
      query: supplierRequests.familyAuditLog,
    }),
    getSupplierFamilyCandidates: builder.query<SupplierFamilyPage, SupplierFamilyCandidateParams | void>({
      providesTags: [supplierCandidateTag],
      query: params => supplierRequests.candidates(params ?? {}),
      transformResponse: (response: {
        items: { fs_entry_id: string; name: string; can_edit: boolean }[];
        total: number;
        offset: number;
        limit: number;
      }) => ({
        ...response,
        items: response.items.map(folder => ({
          id: folder.fs_entry_id,
          fs_entry_id: folder.fs_entry_id,
          name: folder.name,
          external_id: null,
          description: null,
          estimated_total_contract_value: null,
          estimated_annual_contract_value: null,
          currency_code: null,
          procurement_status: null,
          divisions: [],
          accessible_document_count: 0,
          contact_mode: "inherited" as const,
          contact_name: null,
          contact_email: null,
          contact_phone: null,
          can_edit: folder.can_edit,
          current_supplier_id: null,
          current_supplier_name: null,
        })),
      }),
    }),
    createSupplier: builder.mutation<SupplierDetail, SupplierCreateRequest>({
      invalidatesTags: result => [
        supplierListTag,
        supplierOwnerOptionsTag,
        ...(result ? [supplierDetailTag(result.id)] : []),
      ],
      query: supplierRequests.create,
    }),
    updateSupplier: builder.mutation<SupplierDetail, SupplierUpdateArg>({
      invalidatesTags: (_result, _error, { supplierId }) => supplierInvalidationTags.detail(supplierId),
      query: supplierRequests.update,
    }),
    archiveSupplier: builder.mutation<SupplierDetail, string>({
      invalidatesTags: (_result, _error, supplierId) => supplierInvalidationTags.detail(supplierId),
      query: supplierRequests.archive,
    }),
    restoreSupplier: builder.mutation<SupplierDetail, string>({
      invalidatesTags: (_result, _error, supplierId) => supplierInvalidationTags.detail(supplierId),
      query: supplierRequests.restore,
    }),
    assignSupplierFamily: builder.mutation<SupplierFamilyPage["items"][number], SupplierAssignmentArg>({
      invalidatesTags: (_result, _error, args) => supplierInvalidationTags.assignment(args),
      query: supplierRequests.assign,
    }),
    createSupplierUploadFolder: builder.mutation<SupplierFamilyPage["items"][number], SupplierUploadFolderArg>({
      invalidatesTags: (_result, _error, { supplierId }) => supplierInvalidationTags.detach(supplierId),
      query: supplierRequests.createUploadFolder,
    }),
    detachSupplierFamily: builder.mutation<void, SupplierFamilyArg>({
      invalidatesTags: (_result, _error, { supplierId }) => supplierInvalidationTags.detach(supplierId),
      query: supplierRequests.detach,
    }),
    updateSupplierFamilyMetadata: builder.mutation<SupplierFamilyPage["items"][number], SupplierFamilyMetadataArg>({
      invalidatesTags: (_result, _error, { supplierId }) => supplierInvalidationTags.familyMetadata(supplierId),
      query: supplierRequests.updateFamilyMetadata,
    }),
    updateSupplierContacts: builder.mutation<SupplierDetail, SupplierContactsArg>({
      invalidatesTags: (_result, _error, { supplierId }) => supplierInvalidationTags.contacts(supplierId),
      query: supplierRequests.contacts,
    }),
    addSupplierFamilyNote: builder.mutation<ContractFamilyNotePage["items"][number], SupplierFamilyNoteCreateArg>({
      invalidatesTags: (_result, _error, { supplierId, familyId }) =>
        supplierInvalidationTags.familyNotes(supplierId, familyId),
      query: supplierRequests.addFamilyNote,
    }),
  }),
});

export const {
  useGetSupplierOwnerOptionsQuery,
  useGetSupplierFamilyCategoriesQuery,
  useGetSuppliersQuery,
  useLazyGetSuppliersQuery,
  useGetSupplierQuery,
  useGetSupplierFamiliesQuery,
  useGetSupplierContractFolderQuery,
  useGetSupplierContractFolderByFseQuery,
  useGetSupplierContractValuesQuery,
  useLazyGetSupplierFamiliesQuery,
  useGetSupplierContactsPreviewQuery,
  useGetSupplierFamilyNotesQuery,
  useGetSupplierFamilyAuditLogQuery,
  useLazyGetSupplierContactsPreviewQuery,
  useGetSupplierFamilyCandidatesQuery,
  useLazyGetSupplierFamilyCandidatesQuery,
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
  useArchiveSupplierMutation,
  useRestoreSupplierMutation,
  useAssignSupplierFamilyMutation,
  useCreateSupplierUploadFolderMutation,
  useDetachSupplierFamilyMutation,
  useUpdateSupplierFamilyMetadataMutation,
  useUpdateSupplierContactsMutation,
  useAddSupplierFamilyNoteMutation,
} = suppliersApi;
